// tslint:disable:max-line-length

import 'es6-promise'; // Polyfill for ES6 Promises (e.g. for Internet Explorer)
import 'whatwg-fetch'; // Polyfill for Fetch API (e.g. for Internet Explorer)

export class ApiError extends Error {
    public isSessionInvalid: boolean;

    constructor(message: string) {
        super(message);
        this.isSessionInvalid = message === 'WC_E_INVALID_SESSION';
    }
}

async function apiCall<I, O>(method: string, endpoint: string, signal?: AbortSignal, body?: I): Promise<O> {
    const options: RequestInit = {
        method,
        headers: [
            [ 'Content-Type', 'application/json' ],
        ],
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : undefined,
        signal,
    };

    const response: Response = await fetch(endpoint, options);
    if (!response) {
        throw new Error('API call failed');
    }

    if (response.status !== 200) {
        throw new ApiError('2305'); // Error communicating with server
    }

    const { error, message, result } = await response.json();
    if (error || message) {
        throw new ApiError(error! || message!);
    }

    return result!;
}


const mockAbortController: AbortController = {
    abort: () => {
        console.log('Note: AbortController is unsupported in this browser');
        throw new ApiError('341'); // Operation failed
    },
    signal: undefined as any as AbortSignal, // tslint:disable-line:no-any
};

export const makeAbortController = () => {
    try {
        return new AbortController();
    }
    catch (err) {
        // Silently ignore if browser doesn't support AbortController
        return mockAbortController;
    }
};

const TEN_SECONDS = 10 * 1000;

function timeLimitedApiCall<I, O>(method: string, endpoint: string, body?: I): Promise<O> {
    let controller: AbortController | null = makeAbortController();

    return new Promise<O>((resolve, reject) => {
        // Fail the call if it takes longer than 10 seconds
        setTimeout(() => {
            if (controller) {
                try {
                    controller.abort();
                }
                catch (err) {
                    reject(err);
                }
            }
        }, TEN_SECONDS);

        apiCall<I, O>(method, endpoint, controller!.signal, body).then(
            result => {
                controller = null;
                resolve(result);
            },
            err => {
                controller = null;
                reject(err);
            }
        );
    });
}


export const apiGet = async <O = never>(endpoint: string) => await apiCall<never, O>('GET', endpoint);
export const apiPut = async <I, O = never>(endpoint: string, putData?: I) => await apiCall<I, O>('PUT', endpoint, undefined, putData);
export const apiPost = async <I, O = never>(endpoint: string, postData?: I) => await apiCall<I, O>('POST', endpoint, undefined, postData);
export const apiDelete = async <O = never>(endpoint: string) => await apiCall<never, O>('DELETE', endpoint);

export const abortableApiGet = async <O = never>(endpoint: string, signal: AbortSignal) => await apiCall<never, O>('GET', endpoint, signal);
export const abortableApiPut = async <I, O = never>(endpoint: string, signal: AbortSignal, putData?: I) => await apiCall<I, O>('PUT', endpoint, signal, putData);
export const abortableApiPost = async <I, O = never>(endpoint: string, signal: AbortSignal, postData?: I) => await apiCall<I, O>('POST', endpoint, signal, postData);
export const abortableApiDelete = async <O = never>(endpoint: string, signal: AbortSignal) => await apiCall<never, O>('DELETE', endpoint, signal);

export const timeLimitedApiGet = async <O = never>(endpoint: string) => await timeLimitedApiCall<never, O>('GET', endpoint);
export const timeLimitedApiPut = async <I, O = never>(endpoint: string, putData?: I) => await timeLimitedApiCall<I, O>('PUT', endpoint, putData);
export const timeLimitedApiPost = async <I, O = never>(endpoint: string, postData?: I) => await timeLimitedApiCall<I, O>('POST', endpoint, postData);
export const timeLimitedApiDelete = async <O = never>(endpoint: string) => await timeLimitedApiCall<never, O>('DELETE', endpoint);




// WEBPACK FOOTER //
// ./src/api/fetch.ts